import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  apiClient,
  MarketInstrumentItem,
  MarketInstrumentsResponse,
  MarketStateRecordItem,
  MarketStateResponse,
  TrendMatrixProjectionResponse,
  MtcWatchlistItemData,
  MtcWatchlistResponse,
  VolatilityItemData,
  VolatilityStatisticsResponse,
  DataContinuityItemData,
  DataContinuityResponse,
} from '../../api/client';

export type MarketSection = 'trend_matrix' | 'state' | 'mtc' | 'volatility' | 'continuity' | 'instruments';
export type MarketUniverse = 'crypto' | 'stock_token' | 'index';

import { CryptoTickerBar } from '../../components/common';
import {
  VENUE_METADATA,
  CanonicalSpotSymbol,
} from '../../shared/constants/watchlist';
import { CRYPTO_BILINGUAL } from '../../shared/constants/bilingualTitles';

function formatNumberWithCommas(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === '' || val === '--') return '--';
  const str = String(val).trim();
  if (str === '' || str === '--') return '--';
  const parts = str.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

function formatPercentString(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === '' || val === '--') return '--%';
  const str = String(val).trim();
  if (str.endsWith('%')) return str;
  return `${str}%`;
}

export const MultiAssetMarketBoardPreview: React.FC = () => {
  const [section, setSection] = useState<MarketSection>('trend_matrix');
  const [universe, setUniverse] = useState<MarketUniverse>('crypto');
  const [timezone, setTimezone] = useState<'UTC' | 'Asia/Shanghai' | 'Local'>('UTC');
  const [countdown, setCountdown] = useState<number>(5);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Authoritative SSOT State Payloads
  const [instrumentsData, setInstrumentsData] = useState<MarketInstrumentsResponse | null>(null);
  const [stateData, setStateData] = useState<MarketStateResponse | null>(null);
  const [trendMatrixData, setTrendMatrixData] = useState<TrendMatrixProjectionResponse | null>(null);
  const [mtcData, setMtcData] = useState<MtcWatchlistResponse | null>(null);
  const [volatilityData, setVolatilityData] = useState<VolatilityStatisticsResponse | null>(null);
  const [continuityData, setContinuityData] = useState<DataContinuityResponse | null>(null);

  const [selectedStateSymbol, setSelectedStateSymbol] = useState<string>('BTCUSDT');

  // Aggregated data quality state across endpoints
  const aggregateQualityState = useMemo<'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty'>(() => {
    if (errorMessage && !stateData && !trendMatrixData) return 'disconnected';
    if (loading && !stateData && !trendMatrixData) return 'loading';
    const states = [
      stateData?.data_quality_state,
      trendMatrixData?.data_quality_state,
      mtcData?.data_quality_state,
      volatilityData?.data_quality_state,
      continuityData?.data_quality_state,
      instrumentsData?.data_quality_state,
    ].filter(Boolean);

    if (states.includes('disconnected')) return 'disconnected';
    if (states.includes('empty')) return 'empty';
    if (states.includes('stale')) return 'stale';
    if (states.includes('loading')) return 'loading';
    if (states.includes('fresh')) return 'fresh';
    return 'loading';
  }, [errorMessage, loading, stateData, trendMatrixData, mtcData, volatilityData, continuityData, instrumentsData]);

  // Authoritative fetch logic
  const fetchLiveData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const [instRes, stateRes, matrixRes, mtcRes, volRes, contRes] = await Promise.allSettled([
        apiClient.getMarketInstruments(universe),
        apiClient.getMarketState(universe === 'crypto' ? undefined : ''),
        apiClient.getMarketTrendMatrix(universe),
        apiClient.getMarketMtcWatchlist(),
        apiClient.getMarketVolatilityStats(),
        apiClient.getMarketContinuity(),
      ]);

      if (instRes.status === 'fulfilled') setInstrumentsData(instRes.value);
      if (stateRes.status === 'fulfilled') {
        setStateData(stateRes.value);
        if (stateRes.value.records.length > 0 && !selectedStateSymbol) {
          setSelectedStateSymbol(stateRes.value.records[0].symbol);
        }
      }
      if (matrixRes.status === 'fulfilled') setTrendMatrixData(matrixRes.value);
      if (mtcRes.status === 'fulfilled') setMtcData(mtcRes.value);
      if (volRes.status === 'fulfilled') setVolatilityData(volRes.value);
      if (contRes.status === 'fulfilled') setContinuityData(contRes.value);

      const rejections = [instRes, stateRes, matrixRes, mtcRes, volRes, contRes].filter(
        (r) => r.status === 'rejected'
      );
      if (rejections.length === 6) {
        setErrorMessage('无法连接至 Market-Hub 行情服务，网络断开或网关不可达 (DISCONNECTED)');
      }
    } catch (e: any) {
      console.error('Stage 056 fetchLiveData error:', e);
      setErrorMessage(e?.message || '获取多资产行情看板数据异常');
    } finally {
      setLoading(false);
      setCountdown(5);
    }
  }, [universe, selectedStateSymbol]);

  useEffect(() => {
    fetchLiveData();
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchLiveData();
          return 5;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchLiveData]);

  // Selected state item for 8-dimension deep dive
  const selectedStateItem = useMemo<MarketStateRecordItem | null>(() => {
    if (!stateData?.records || stateData.records.length === 0) return null;
    return stateData.records.find((r) => r.symbol === selectedStateSymbol) || stateData.records[0];
  }, [stateData, selectedStateSymbol]);

  const renderQualityBadge = (quality: string | undefined) => {
    const q = (quality || 'loading').toLowerCase();
    const badgeClass =
      q === 'fresh'
        ? 'badge-bullish'
        : q === 'stale'
        ? 'badge-amber'
        : q === 'disconnected'
        ? 'badge-bearish'
        : 'badge-neutral';
    return (
      <span className={`badge ${badgeClass}`} style={{ fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
        ● {q.toUpperCase()}
      </span>
    );
  };

  const formatTimestamp = (iso?: string | null) => {
    if (!iso) return '--';
    if (timezone === 'UTC') return `${iso.replace('T', ' ').slice(0, 19)} UTC`;
    try {
      const d = new Date(iso);
      if (timezone === 'Asia/Shanghai') {
        return d.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false });
      }
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const getVenueInfo = (sym: string) => {
    const meta = VENUE_METADATA[sym as CanonicalSpotSymbol];
    if (meta) {
      return { venue: meta.venue, className: meta.badgeClass };
    }
    return { venue: 'CRYPTO', className: 'venue-binance' };
  };

  return (
    <div className="view-panel active" id="view-market-board-preview" role="tabpanel">
      {/* 1. TSO 2.0 Standard View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <span style={{ color: 'var(--color-emerald)', fontSize: '18px' }}>📊</span>
            <h2 className="bilingual-header-title-zh">{CRYPTO_BILINGUAL.marketBoard.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en">{CRYPTO_BILINGUAL.marketBoard.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            7 标的 × 6 周期跨资产 SSOT · 100% 纯 Decimal 算子 · 严格 5 态流转契约 (Fresh / Stale / Disconnected / Empty)
          </p>
        </div>

        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, whiteSpace: 'nowrap' }}>
          {renderQualityBadge(aggregateQualityState)}

          <div className="market-type-toggle" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            <button
              className={`toggle-btn ${timezone === 'UTC' ? 'active' : ''}`}
              onClick={() => setTimezone('UTC')}
            >
              UTC
            </button>
            <button
              className={`toggle-btn ${timezone === 'Asia/Shanghai' ? 'active' : ''}`}
              onClick={() => setTimezone('Asia/Shanghai')}
            >
              CST
            </button>
            <button
              className={`toggle-btn ${timezone === 'Local' ? 'active' : ''}`}
              onClick={() => setTimezone('Local')}
            >
              Local
            </button>
          </div>

          <span
            className="venue-pill venue-binance"
            style={{
              fontSize: '11px',
              padding: '3px 8px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              minWidth: '32px',
              textAlign: 'center',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              height: '24px',
            }}
            title="自动轮询倒计时"
          >
            {countdown}s
          </span>

          <button
            className="toggle-btn"
            id="btn-refresh-market-board"
            onClick={fetchLiveData}
            disabled={loading}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              padding: '0 12px',
              height: '24px',
              lineHeight: '22px',
              minWidth: '76px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            {loading ? '刷新中...' : '手动刷新'}
          </button>
        </div>
      </div>

      {/* Disconnected error banner if any */}
      {errorMessage && (
        <div
          style={{
            background: 'var(--alert-bearish-bg)',
            border: '1px solid var(--alert-bearish-border)',
            borderRadius: '6px',
            padding: '8px 14px',
            marginBottom: '4px',
            color: 'var(--color-bearish)',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>⚠️ {errorMessage}</span>
          <button
            className="toggle-btn"
            onClick={fetchLiveData}
            style={{
              border: '1px solid var(--color-bearish)',
              color: 'var(--color-bearish)',
              padding: '2px 8px',
              fontSize: '11px',
            }}
          >
            重试
          </button>
        </div>
      )}

      {/* 2. Subtab Navigation & Universe Selector */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          margin: '2px 0 6px 0',
        }}
      >
        <div className="market-type-toggle">
          {[
            { id: 'trend_matrix', labelZh: '1. 趋势矩阵', labelEn: 'TREND MATRIX (7×6)' },
            { id: 'state', labelZh: '2. 实时综合状态', labelEn: `REALTIME STATE (${stateData?.records?.length ?? 0})` },
            { id: 'mtc', labelZh: '3. MTC 多时框共识', labelEn: 'MTC CONSENSUS' },
            { id: 'volatility', labelZh: '4. 波动率统计', labelEn: 'VOLATILITY STATS' },
            { id: 'continuity', labelZh: '5. 时序连续性监控', labelEn: 'DATA CONTINUITY' },
            { id: 'instruments', labelZh: '6. 标的资产目录', labelEn: `UNIVERSE CATALOG (${instrumentsData?.items?.length ?? 0})` },
          ].map((tab) => (
            <button
              key={tab.id}
              className={`toggle-btn bilingual-tab-btn ${section === tab.id ? 'active' : ''}`}
              onClick={() => setSection(tab.id as MarketSection)}
            >
              <div className="bilingual-tab-title-zh">{tab.labelZh}</div>
              <span className="bilingual-tab-subtitle-en">{tab.labelEn}</span>
            </button>
          ))}
        </div>

        <div className="market-type-toggle">
          {[
            { id: 'crypto', labelZh: 'Crypto 主力池', labelEn: 'CRYPTO CORE POOL' },
          ].map((u) => (
            <button
              key={u.id}
              className={`toggle-btn bilingual-tab-btn ${universe === u.id ? 'active' : ''}`}
              onClick={() => setUniverse(u.id as MarketUniverse)}
            >
              <div className="bilingual-tab-title-zh">{u.labelZh}</div>
              <span className="bilingual-tab-subtitle-en">{u.labelEn}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 3. TSO 2.0 Standard Digest Bar */}
      <div className="digest-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span>只读端点: </span>
          <code className="digest-code">
            GET /api/v1/market/{section === 'state' ? 'state' : section === 'trend_matrix' ? 'trend-matrix' : section === 'mtc' ? 'mtc-watchlist' : section === 'volatility' ? 'volatility-statistics' : section === 'continuity' ? 'continuity' : 'instruments'}
          </code>
          <span style={{ color: 'var(--border-subtle)' }}>|</span>
          <span style={{ color: 'var(--text-secondary)' }}>
            {section === 'trend_matrix' && 'TrendWeather 投影快照 · 7 标的 × 6 周期态标签与动能评分'}
            {section === 'state' && '8 维市场行为学/心理学维度得分 (Pure Decimal 0~100) · 点击行展开 8 维深潜'}
            {section === 'mtc' && '多时框共识引擎 · 周期对齐比例与冲突检测'}
            {section === 'volatility' && "Wilder's RMA ATR(14) 与 30 日实现波动率 (RV) · 波动率状态机"}
            {section === 'continuity' && '采集引擎连续性 · 24h 缺棒率 · 时钟漂移与因果时延监测'}
            {section === 'instruments' && '多资产统一标的元数据目录 · 精度与最小跳价规范'}
          </span>
        </div>
        <div>
          因果闭合判定: <strong style={{ color: 'var(--color-bullish)' }}>严格闭合烛线 (t) · W ≥ 3s</strong>
        </div>
      </div>

      {/* 4. Active Section Table View */}
      <section className="tags-matrix-panel" style={{ padding: '0', overflow: 'hidden' }}>
        {/* Section 1: Watchlist Trend Matrix */}
        {section === 'trend_matrix' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="matrix-table" id="market-trend-matrix-table">
              <thead>
                <tr>
                  <th style={{ width: '95px' }}>标的资产</th>
                  {(trendMatrixData?.timeframes ?? ['5m', '15m', '30m', '1h', '4h', '1d']).map((tf) => {
                    const isTactical = ['5m', '15m'].includes(tf);
                    const isPrimary = ['30m', '1h'].includes(tf);
                    const tierLabel = isTactical ? '战术' : isPrimary ? '主航道' : '战略';
                    const tierColor = isTactical ? 'var(--color-cyan)' : isPrimary ? '#7e22ce' : '#b45309';
                    return (
                      <th key={tf} style={{ minWidth: '130px' }}>
                        <div>{tf}</div>
                        <span style={{ fontSize: '9px', color: tierColor, fontWeight: 600 }}>{tierLabel}</span>
                      </th>
                    );
                  })}
                  <th style={{ width: '100px' }}>综合质量</th>
                </tr>
              </thead>
              <tbody>
                {(!trendMatrixData || trendMatrixData.symbols.length === 0) ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {loading ? '趋势矩阵数据加载中 (LOADING)...' : '暂无趋势矩阵数据 (EMPTY)'}
                    </td>
                  </tr>
                ) : (
                  trendMatrixData.symbols.map((sym: string) => {
                    const vInfo = getVenueInfo(sym);
                    return (
                      <tr key={sym}>
                        <td>
                          <div className="symbol-cell" title={`流动性主市场: ${vInfo.venue}`}>
                            <span className="symbol-name">{sym}</span>
                          </div>
                        </td>
                        {(trendMatrixData.timeframes ?? ['5m', '15m', '30m', '1h', '4h', '1d']).map((tf) => {
                          const cell = trendMatrixData.matrix?.[sym]?.[tf];
                          if (!cell) {
                            return (
                              <td key={tf} style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                                --
                              </td>
                            );
                          }
                          const isUp = cell.trend_direction === 'BULLISH';
                          const isDown = cell.trend_direction === 'BEARISH';
                          const cardBorder = isUp
                            ? '1px solid rgba(5, 150, 105, 0.40)'
                            : isDown
                            ? '1px solid rgba(225, 29, 72, 0.40)'
                            : '1px solid rgba(217, 119, 6, 0.40)';
                          const cardBg = 'var(--bg-surface)';
                          const trendColor = isUp
                            ? 'var(--color-bullish)'
                            : isDown
                            ? 'var(--color-bearish)'
                            : 'var(--color-warning)';
                          return (
                            <td key={tf}>
                              <div
                                style={{
                                  padding: '6px 8px',
                                  borderRadius: '6px',
                                  background: cardBg,
                                  border: cardBorder,
                                }}
                              >
                                <div style={{ fontWeight: 700, fontSize: '11px', color: trendColor }}>
                                  {cell.trend_label.replace(/_/g, ' ')}
                                </div>
                                <div
                                  style={{
                                    fontSize: '10px',
                                    fontFamily: 'var(--font-mono)',
                                    color: 'var(--text-secondary)',
                                    marginTop: '2px',
                                  }}
                                >
                                  得分: {cell.score ?? '--'}
                                </div>
                                <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '1px' }}>
                                  {cell.ema_alignment}
                                </div>
                              </div>
                            </td>
                          );
                        })}
                        <td>{renderQualityBadge(trendMatrixData.data_quality_state)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Section 2: Realtime Comprehensive State */}
        {section === 'state' && (
          <div>
            {universe === 'crypto' && (
              <div style={{ marginBottom: '12px' }}>
                <CryptoTickerBar
                  selectedSymbol={selectedStateSymbol}
                  onSelectSymbol={(sym) => setSelectedStateSymbol(sym)}
                />
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table className="matrix-table" id="market-state-table">
                <thead>
                  <tr>
                    <th style={{ width: '95px' }}>标的资产</th>
                    <th style={{ textAlign: 'right' }}>最新价</th>
                    <th style={{ textAlign: 'right' }}>24h 涨跌幅</th>
                    <th style={{ textAlign: 'right' }}>24h 最高 / 最低</th>
                    <th style={{ textAlign: 'right' }}>24h 成交量</th>
                    <th style={{ textAlign: 'right' }}>盘口点差</th>
                    <th style={{ textAlign: 'center' }}>市场行为学状态</th>
                    <th style={{ textAlign: 'center' }}>更新时间</th>
                    <th style={{ textAlign: 'center' }}>数据质量</th>
                  </tr>
                </thead>
                <tbody>
                  {(!stateData || stateData.records.length === 0) ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                        {loading ? '实时行情状态加载中 (LOADING)...' : '暂无行情数据 (EMPTY)'}
                      </td>
                    </tr>
                  ) : (
                    stateData.records.map((item: MarketStateRecordItem) => {
                      const isSelected = selectedStateSymbol === item.symbol;
                      const isUp = Boolean(item.change_24h_pct && (item.change_24h_pct.startsWith('+') || (!item.change_24h_pct.startsWith('-') && item.change_24h_pct !== '0.00' && item.change_24h_pct !== '0.00%')));
                      const vInfo = getVenueInfo(item.symbol);
                      return (
                        <tr
                          key={item.symbol}
                          onClick={() => setSelectedStateSymbol(item.symbol)}
                          style={{
                            cursor: 'pointer',
                            background: isSelected ? 'var(--accent-cyan-glow)' : 'transparent',
                            transition: 'background 0.15s ease',
                          }}
                        >
                          <td>
                            <div className="symbol-cell" title={`流动性主市场: ${vInfo.venue}`}>
                              <span className="symbol-name">{item.symbol}</span>
                              {isSelected && <span style={{ fontSize: '10px', color: 'var(--accent-cyan)' }}>● 展开</span>}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-primary)' }}>
                            {item.last_price ? `$${formatNumberWithCommas(item.last_price)}` : '--'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: item.change_24h_pct ? (isUp ? 'var(--color-bullish)' : 'var(--color-bearish)') : 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {formatPercentString(item.change_24h_pct)}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                            {item.high_24h && item.low_24h ? `$${formatNumberWithCommas(item.high_24h)} / $${formatNumberWithCommas(item.low_24h)}` : '--'}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{formatNumberWithCommas(item.volume_24h)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                            {item.spread_bps ? `${formatNumberWithCommas(item.spread_bps)} bps` : '--'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              className={`badge ${isUp ? 'badge-bullish' : 'badge-bearish'}`}
                              style={{ fontSize: '10px' }}
                            >
                              {item.market_state.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {formatTimestamp(item.bar_close_utc)}
                          </td>
                          <td style={{ textAlign: 'center' }}>{renderQualityBadge(item.data_quality_state)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* 8-Dimension Behavioral Deep Dive Panel */}
            {selectedStateItem && (
              <div
                style={{
                  padding: '14px 18px',
                  borderTop: '1px solid var(--border-subtle)',
                  background: 'var(--bg-canvas)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                      🔍 {selectedStateItem.symbol} 市场行为学与心理学 8 维状态深潜
                    </span>
                    <span className="venue-pill venue-hyperliquid" style={{ fontSize: '9px' }}>
                      Pure Decimal 0~100
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                    综合状态: <strong style={{ color: 'var(--accent-cyan)' }}>{selectedStateItem.market_state}</strong> | 快照: {formatTimestamp(selectedStateItem.snapshot_as_of_utc)}
                  </div>
                </div>

                {selectedStateItem.dimensions ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                      gap: '8px',
                    }}
                  >
                    {[
                      { key: 'participation', labelZh: '市场参与度', labelEn: 'PARTICIPATION', desc: '成交量能与挂单密度支撑', score: selectedStateItem.dimensions.participation ?? null },
                      { key: 'directional_intent', labelZh: '方向意向强度', labelEn: 'DIRECTIONAL INTENT', desc: '主买/主卖单向冲击力', score: selectedStateItem.dimensions.directional_intent ?? null },
                      { key: 'conviction', labelZh: '多空信念度', labelEn: 'CONVICTION', desc: '大单持仓稳定与无毛刺延续', score: selectedStateItem.dimensions.conviction ?? null },
                      { key: 'risk_appetite', labelZh: '风险偏好水平', labelEn: 'RISK APPETITE', desc: '追高买入意愿与杠杆扩张', score: selectedStateItem.dimensions.risk_appetite ?? null },
                      { key: 'acceptance', labelZh: '价值区接受度', labelEn: 'ACCEPTANCE', desc: '新价格区间内的成交认可度', score: selectedStateItem.dimensions.acceptance ?? null },
                      { key: 'volatility_expansion', labelZh: '波动率扩张', labelEn: 'VOLATILITY EXPANSION', desc: 'ATR通道发散与爆发力', score: selectedStateItem.dimensions.volatility_expansion ?? null },
                      { key: 'trend_persistence', labelZh: '趋势持续惯性', labelEn: 'TREND PERSISTENCE', desc: '多周期EMA排列顺畅度', score: selectedStateItem.dimensions.trend_persistence ?? null },
                      { key: 'exhaustion', labelZh: '动能衰竭度', labelEn: 'EXHAUSTION', desc: '顶底背离与量价动能脱节', score: selectedStateItem.dimensions.exhaustion ?? null },
                    ].map((d) => {
                      const intScore = d.score !== null ? (parseInt(d.score, 10) || 0) : null;
                      const isHighExhaustion = d.key === 'exhaustion' && intScore !== null && intScore > 50;
                      return (
                        <div
                          key={d.key}
                          style={{
                            background: 'var(--bg-card)',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid var(--border-subtle)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: '11px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '11px' }}>{d.labelZh}</span>
                              <span style={{ fontSize: '8px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d.labelEn}</span>
                            </div>
                            <strong
                              style={{
                                fontFamily: 'var(--font-mono)',
                                color: isHighExhaustion ? 'var(--color-bearish)' : 'var(--accent-cyan)',
                              }}
                            >
                              {d.score !== null ? `${d.score} / 100` : '--'}
                            </strong>
                          </div>
                          <div
                            style={{
                              width: '100%',
                              height: '4px',
                              background: 'var(--border-subtle)',
                              borderRadius: '2px',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: intScore !== null ? `${Math.min(100, Math.max(0, intScore))}%` : '0%',
                                height: '100%',
                                background: isHighExhaustion
                                  ? 'var(--color-bearish)'
                                  : 'var(--accent-cyan)',
                                borderRadius: '2px',
                              }}
                            />
                          </div>
                          <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '3px' }}>{d.desc}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px' }}>
                    该标的暂无行为学 8 维分解指标 (EMPTY)
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Section 3: MTC Watchlist */}
        {section === 'mtc' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="matrix-table" id="market-mtc-table">
              <thead>
                <tr>
                  <th style={{ width: '95px' }}>标的代码</th>
                  <th style={{ textAlign: 'center' }}>综合共识方向</th>
                  <th style={{ textAlign: 'right' }}>共识评分 (0~100)</th>
                  <th style={{ textAlign: 'center' }}>置信度水平</th>
                  <th style={{ textAlign: 'right' }}>周期一致性</th>
                  <th style={{ textAlign: 'center' }}>冲突水平</th>
                  <th style={{ textAlign: 'center' }}>对齐周期数</th>
                  <th style={{ textAlign: 'left' }}>宏观形态概要</th>
                  <th style={{ textAlign: 'center' }}>数据质量</th>
                </tr>
              </thead>
              <tbody>
                {(!mtcData || mtcData.items.length === 0) ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {loading ? 'MTC 多时框共识数据加载中 (LOADING)...' : '暂无 MTC 共识数据 (EMPTY)'}
                    </td>
                  </tr>
                ) : (
                  mtcData.items.map((item: MtcWatchlistItemData) => {
                    const isLong = item.consensus_direction === 'LONG';
                    const isShort = item.consensus_direction === 'SHORT';
                    const vInfo = getVenueInfo(item.symbol);
                    return (
                      <tr key={item.symbol}>
                        <td>
                          <div className="symbol-cell" title={`流动性主市场: ${vInfo.venue}`}>
                            <span className="symbol-name">{item.symbol}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            className={`badge ${isLong ? 'badge-bullish' : isShort ? 'badge-bearish' : 'badge-neutral'}`}
                            style={{ fontSize: '10px' }}
                          >
                            {isLong ? '↑ LONG 看多' : isShort ? '↓ SHORT 看空' : '→ NEUTRAL 震荡'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {item.consensus_score}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: '11px',
                              color: item.confidence === 'HIGH' ? 'var(--color-bullish)' : item.confidence === 'MEDIUM' ? 'var(--color-amber)' : 'var(--text-muted)',
                            }}
                          >
                            {item.confidence}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {item.agreement_ratio}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            className={`badge ${item.conflict_level === 'HIGH' ? 'badge-bearish' : 'badge-neutral'}`}
                            style={{ fontSize: '10px' }}
                          >
                            {item.conflict_level}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
                          {item.aligned_timeframes_count} / {item.total_timeframes_count}
                        </td>
                        <td style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: '11px' }}>
                          {item.regime_summary}
                        </td>
                        <td style={{ textAlign: 'center' }}>{renderQualityBadge(item.data_quality_state)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Section 4: Volatility Statistics */}
        {section === 'volatility' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="matrix-table" id="market-volatility-table">
              <thead>
                <tr>
                  <th style={{ width: '95px' }}>标的代码</th>
                  <th style={{ textAlign: 'center' }}>时钟周期</th>
                  <th style={{ textAlign: 'right' }}>真实波幅 ATR(14)</th>
                  <th style={{ textAlign: 'right' }}>ATR 比例 (ATR%)</th>
                  <th style={{ textAlign: 'right' }}>30日实现波动率</th>
                  <th style={{ textAlign: 'right' }}>波动率分位数</th>
                  <th style={{ textAlign: 'center' }}>波动率状态</th>
                  <th style={{ textAlign: 'right' }}>样本烛线数</th>
                  <th style={{ textAlign: 'center' }}>更新时间</th>
                  <th style={{ textAlign: 'center' }}>数据质量</th>
                </tr>
              </thead>
              <tbody>
                {(!volatilityData || volatilityData.items.length === 0) ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {loading ? '波动率统计数据加载中 (LOADING)...' : '暂无波动率统计数据 (EMPTY)'}
                    </td>
                  </tr>
                ) : (
                  volatilityData.items.map((item: VolatilityItemData) => {
                    const vInfo = getVenueInfo(item.symbol);
                    return (
                      <tr key={`${item.symbol}-${item.timeframe}`}>
                        <td>
                          <div className="symbol-cell" title={`流动性主市场: ${vInfo.venue}`}>
                            <span className="symbol-name">{item.symbol}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{item.timeframe}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                          {item.atr_14 ? `$${item.atr_14}` : '--'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {item.atr_pct ? `${item.atr_pct}%` : '--'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {item.realized_volatility_30d_pct ? `${item.realized_volatility_30d_pct}%` : '--'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--color-amber)', fontWeight: 700 }}>
                          {item.vol_percentile ? `${item.vol_percentile}%` : '--'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                            {item.volatility_regime}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{item.sample_bars_count}</td>
                        <td style={{ textAlign: 'center', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {formatTimestamp(item.bar_close_utc)}
                        </td>
                        <td style={{ textAlign: 'center' }}>{renderQualityBadge(item.data_quality_state)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Section 5: Data Continuity Telemetry */}
        {section === 'continuity' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="matrix-table" id="market-continuity-table">
              <thead>
                <tr>
                  <th style={{ width: '95px' }}>标的代码</th>
                  <th style={{ textAlign: 'center' }}>数据流状态</th>
                  <th style={{ textAlign: 'center' }}>心跳机状态</th>
                  <th style={{ textAlign: 'right' }}>24h 缺失 K 线数</th>
                  <th style={{ textAlign: 'right' }}>时钟漂移延迟</th>
                  <th style={{ textAlign: 'right' }}>入库时延</th>
                  <th style={{ textAlign: 'right' }}>24h 完整性比例</th>
                  <th style={{ textAlign: 'center' }}>最近入库周期 (UTC)</th>
                  <th style={{ textAlign: 'center' }}>数据质量</th>
                </tr>
              </thead>
              <tbody>
                {(!continuityData || continuityData.items.length === 0) ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {loading ? '时序连续性数据加载中 (LOADING)...' : '暂无时序监控数据 (EMPTY)'}
                    </td>
                  </tr>
                ) : (
                  continuityData.items.map((item: DataContinuityItemData) => {
                    const isHealthy = item.feed_status === 'HEALTHY';
                    const isDegraded = item.feed_status === 'DEGRADED';
                    const feedBadge = isHealthy ? 'badge-bullish' : isDegraded ? 'badge-amber' : 'badge-bearish';
                    const vInfo = getVenueInfo(item.symbol);
                    return (
                      <tr key={item.symbol}>
                        <td>
                          <div className="symbol-cell" title={`流动性主市场: ${vInfo.venue}`}>
                            <span className="symbol-name">{item.symbol}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            className={`badge ${feedBadge}`}
                            style={{ fontSize: '10px' }}
                          >
                            ● {item.feed_status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="venue-pill venue-binance" style={{ fontSize: '9px' }}>
                            {item.heartbeat_fsm_state}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {item.missing_bars_count_24h !== null && item.missing_bars_count_24h !== undefined ? `${item.missing_bars_count_24h} 根` : '--'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {item.clock_drift_ms !== null && item.clock_drift_ms !== undefined ? `${item.clock_drift_ms} ms` : '--'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {item.ingest_lag_ms !== null && item.ingest_lag_ms !== undefined ? `${item.ingest_lag_ms} ms` : '--'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--color-bullish)' }}>
                          {item.integrity_ratio_24h ?? '--'}
                        </td>
                        <td style={{ textAlign: 'center', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {formatTimestamp(item.last_bar_close_utc)}
                        </td>
                        <td style={{ textAlign: 'center' }}>{renderQualityBadge(item.data_quality_state)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Section 6: Multi-Asset Instruments Catalog */}
        {section === 'instruments' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="matrix-table" id="market-instruments-table">
              <thead>
                <tr>
                  <th style={{ width: '95px' }}>标的代码</th>
                  <th style={{ textAlign: 'left' }}>基础/报价资产</th>
                  <th style={{ textAlign: 'center' }}>资产类别</th>
                  <th style={{ textAlign: 'center' }}>合约类型</th>
                  <th style={{ textAlign: 'center' }}>状态</th>
                  <th style={{ textAlign: 'right' }}>价格精度</th>
                  <th style={{ textAlign: 'right' }}>最小跳价</th>
                  <th style={{ textAlign: 'right' }}>最小下单量</th>
                  <th style={{ textAlign: 'center' }}>数据质量</th>
                </tr>
              </thead>
              <tbody>
                {(!instrumentsData || instrumentsData.items.length === 0) ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {loading ? '标的目录数据加载中 (LOADING)...' : '暂无标的数据 (EMPTY)'}
                    </td>
                  </tr>
                ) : (
                  instrumentsData.items.map((ins: MarketInstrumentItem) => {
                    const vInfo = getVenueInfo(ins.symbol);
                    return (
                      <tr key={ins.symbol}>
                        <td>
                          <div className="symbol-cell" title={`流动性主市场: ${vInfo.venue}`}>
                            <span className="symbol-name">{ins.symbol}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                          {ins.base_asset} / {ins.quote_asset}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="venue-pill venue-okx" style={{ fontSize: '9px' }}>
                            {ins.category.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '11px' }}>
                          {ins.contract_type}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`badge ${ins.status === 'TRADING' ? 'badge-bullish' : ins.status === 'PLANNED' ? 'badge-neutral' : 'badge-amber'}`} style={{ fontSize: '10px' }}>
                            ● {ins.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{ins.price_precision} 位小数</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{ins.tick_size}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{ins.min_qty}</td>
                        <td style={{ textAlign: 'center' }}>{renderQualityBadge(instrumentsData.data_quality_state)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};
